import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useDoohReportFormatters } from '@/components/doohReport/common/hooks/useDoohReportFormatters';
import MetricsInfoModal from '@/components/doohReport/common/MetricsInfoModal/MetricsInfoModal';
import KpiTooltipContent from './KpiTooltipContent';
import MetricBlock from './MetricBlock';
import { getMetricDetailCardsTooltipCopy } from './metricDetailCards.copy';
import OnTargetDetails from './OnTargetDetails';
import UniverseCartographyTags from './UniverseCartographyTags';

import type {
  DoohReportAudience,
  DoohReportMetrics
} from 'sonata-static-storage/lib/entities/doohReport/doohReport.types';
import type { MetricsInfoTopic } from '@/components/doohReport/common/MetricsInfoModal/MetricsInfoModal';

import './metricDetailCards.scss';

type Props = {
  metrics: DoohReportMetrics;
  audience: DoohReportAudience;
};

/** The report contract carries every share as 0-100. */
const RATIO_TO_PERCENT = 100;

const BLOCK_ICONS = {
  impressions: {
    className: 'fa-solid fa-eye',
    color: 'var(--color-ai-primary-700, #1909C6)',
    containerStyle: { background: 'linear-gradient(135deg, #DBE3FF 0%, #CBD6FF 100%)' }
  },
  targetReach: {
    className: 'fa-kit fa-baseline',
    color: 'var(--color-status-enabled-600, #0C6262)',
    containerStyle: { background: 'linear-gradient(135deg, #E2F5F6 0%, #B0F0F3 100%)' }
  },
  targetUniverse: {
    className: 'fa-solid fa-earth-americas',
    // Design gold, with no token: `--color-status-warning-500` resolves to an orange (#FFB041).
    color: '#D39519',
    containerStyle: { background: 'linear-gradient(148deg, #FFF6BD 0%, #F2C659 100%)' }
  }
} as const;

/**
 * Row of three KPI cards opening the "Reach & frequency" section: target universe,
 * on-target impressions against the benchmark, and target reach, average frequency
 * and coverage.
 */
export default function MetricDetailCards({ metrics, audience }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { formatAbbreviated, formatCount, formatDecimal, formatPercent } = useDoohReportFormatters();
  const [infoTopic, setInfoTopic] = useState<MetricsInfoTopic | null>(null);

  const tooltipCopy = getMetricDetailCardsTooltipCopy(formatMessage);
  const coverage =
    metrics.targetReach !== null && metrics.targetUniverse
      ? (metrics.targetReach / metrics.targetUniverse) * RATIO_TO_PERCENT
      : null;
  const labels = {
    avgFrequency: formatMessage({ defaultMessage: 'Avg. frequency', id: 'doohReport.metricDetailCards.avgFrequency' }),
    coverage: formatMessage({ defaultMessage: 'Coverage', id: 'doohReport.metricDetailCards.coverage' }),
    onTarget: formatMessage({ defaultMessage: 'On-target', id: 'doohReport.metricDetailCards.onTarget' }),
    targetReach: formatMessage({ defaultMessage: 'Target reach', id: 'doohReport.metricDetailCards.targetReach' }),
    targetUniverse: formatMessage({
      defaultMessage: 'Target Universe',
      id: 'doohReport.metricDetailCards.targetUniverse'
    })
  };
  const coverageTooltip = formatMessage(
    {
      defaultMessage:
        'Share of your Target Universe reached: Target reach ÷ Target Universe ({reach} ÷ {universe} = {coverage}).',
      id: 'doohReport.metricDetailCards.tooltip.coverage'
    },
    {
      coverage: formatPercent(coverage, 1),
      reach: formatAbbreviated(metrics.targetReach),
      universe: formatCount(metrics.targetUniverse)
    }
  );

  return (
    <div className="metricDetailCards">
      <div className="metricDetailCards__card metricDetailCards__card--universe">
        <div className="metricDetailCards__cardRow">
          <MetricBlock
            icon={BLOCK_ICONS.targetUniverse}
            label={labels.targetUniverse}
            tooltip={<KpiTooltipContent text={tooltipCopy.targetUniverse} title={labels.targetUniverse} />}
            value={formatCount(metrics.targetUniverse)}
          />
          <UniverseCartographyTags cartographies={metrics.universeCartographies} />
        </div>
        {metrics.universeLevel && (
          <span className="metricDetailCards__universeLevel">
            {formatMessage(
              {
                defaultMessage: 'Universe level selected: {level}',
                id: 'doohReport.metricDetailCards.universeLevelSelected'
              },
              { level: metrics.universeLevel }
            )}
          </span>
        )}
      </div>

      <div className="metricDetailCards__card metricDetailCards__card--impressions">
        <MetricBlock
          caption={formatMessage({ defaultMessage: 'Impressions', id: 'doohReport.metricDetailCards.onTargetCaption' })}
          icon={BLOCK_ICONS.impressions}
          label={labels.onTarget}
          value={formatAbbreviated(metrics.targetImpacts)}
        />
        <OnTargetDetails
          baselineOnTargetPct={audience.baselineOnTargetPct}
          benchmarkTooltip={
            <KpiTooltipContent
              tag={formatMessage({ defaultMessage: 'Estimated', id: 'doohReport.metricDetailCards.estimated' })}
              text={tooltipCopy.benchmark}
              title={formatMessage({
                defaultMessage: 'DOOH avg. (benchmark)',
                id: 'doohReport.metricDetailCards.benchmark'
              })}
            />
          }
          onTargetPct={audience.onTargetPct}
          onTargetVsBaselinePts={audience.onTargetVsBaselinePts}
          targetImpacts={metrics.targetImpacts}
        />
      </div>

      <div className="metricDetailCards__card metricDetailCards__card--reach">
        <MetricBlock
          caption={formatMessage({ defaultMessage: 'Unique people', id: 'doohReport.metricDetailCards.reachCaption' })}
          icon={BLOCK_ICONS.targetReach}
          label={labels.targetReach}
          tooltip={<KpiTooltipContent text={tooltipCopy.targetReach} title={labels.targetReach} />}
          value={formatAbbreviated(metrics.targetReach)}
        />
        <div className="metricDetailCards__divider" />
        <MetricBlock
          caption={formatMessage({
            defaultMessage: 'Impacts / Person',
            id: 'doohReport.metricDetailCards.frequencyCaption'
          })}
          label={labels.avgFrequency}
          tooltip={
            <KpiTooltipContent
              text={tooltipCopy.avgFrequency}
              title={formatMessage({
                defaultMessage: 'Average frequency',
                id: 'doohReport.metricDetailCards.averageFrequency'
              })}
            />
          }
          value={formatDecimal(metrics.avgFrequency, 1)}
        />
        <div className="metricDetailCards__divider" />
        <MetricBlock
          caption={formatMessage({
            defaultMessage: 'Reach / Universe',
            id: 'doohReport.metricDetailCards.coverageCaption'
          })}
          footer={
            <button
              className="metricDetailCards__learnMore"
              onClick={() => setInfoTopic('reachFrequency')}
              type="button"
            >
              <i className="fa-regular fa-newspaper" />
              {formatMessage({ defaultMessage: 'Learn more...', id: 'doohReport.metricDetailCards.learnMore' })}
            </button>
          }
          label={labels.coverage}
          tooltip={<KpiTooltipContent text={coverageTooltip} title={labels.coverage} />}
          value={formatPercent(coverage, 1)}
        />
      </div>

      <MetricsInfoModal
        id="metricDetailCards-infoModal"
        onChangeTopic={setInfoTopic}
        onClose={() => setInfoTopic(null)}
        topic={infoTopic}
      />
    </div>
  );
}
